import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import { Theme } from '_lib/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.typography.fontFamily,
    backgroundColor: 'none',
  },
  floatingTitle: {
    position: 'absolute',
    top: 11,
    left: 15,
    fontSize: 16,
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: '#6D819C',
    transform: 'translateY(8px) translateZ(0)',
    transformOrigin: 'bottom left',
    transition: '0.2s',
    pointerEvents: 'none',
  },
  floatingTitleActive: {
    color: theme.colors.primary,
    transform: 'translateX(1px) translateY(-8px) scale(0.75) translateZ(0)',
  },
  floatingTitleError: {
    color: theme.colors.error,
  },
  childInput: {
    width: '100%',
    padding: '25px 15px 9px 15px',
    border: 'none',

    fontSize: 16,
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    textOverflow: 'ellipsis',
    color: theme.colors.title,
    outline: 'none',
    background: 'none',
    boxSizing: 'border-box',
    MozAppearance: 'textfield',

    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      appearance: 'none',
      margin: 0,
    },
  },
  input: {
    flex: 1,
    margin: 0,

    border: '1px solid rgba(0,0,0, 0.1)',
    borderRadius: 4,
    appearance: 'none',
    transition: '0.2s',
    boxShadow: theme.shadow.box,
  },
  inputActive: {
    borderColor: theme.colors.primary,
  },
  inputError: {
    borderColor: theme.colors.error,
  },
  errors: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    padding: '5px 15px',
    fontSize: 11,
    fontWeight: 700,
    color: theme.colors.error,
    pointerEvents: 'none',

    [theme.r.mobile.up]: {
      padding: '9px 15px',
    },
  },
}));

export type FocusEvent = React.FocusEvent<HTMLInputElement | HTMLElement>;

type Props = {
  title?: string;
  value?: string | number;
  error?: string;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  testId?: string;

  children: ({
    onFocus,
    onBlur,
    inputClassName,
  }: {
    onFocus: (e: FocusEvent) => void;
    onBlur: (e: FocusEvent) => void;
    inputClassName: string;
  }) => React.ReactNode;
};

const Base = (props: Props) => {
  const {
    title,
    value,
    onFocus = () => {},
    onBlur = () => {},
    testId,
    error,
    children,
  } = props;
  const classes = useStyles(props);
  const [isFocused, setIsFocused] = useState(false);

  const onChildFocus = (e: FocusEvent) => {
    onFocus(e);
    setIsFocused(true);
  };

  const onChildBlur = (e: FocusEvent) => {
    onBlur(e);
    setIsFocused(false);
  };

  const isValuePopulated = Array.isArray(value)
    ? Boolean(value.length)
    : value || value === 0;

  const floatingTitleStyle = cx(classes.floatingTitle, {
    [classes.floatingTitleActive]: isFocused || isValuePopulated,
    [classes.floatingTitleError]: error,
  });

  const inputStyle = cx(classes.input, {
    [classes.inputActive]: isFocused,
    [classes.inputError]: error,
  });

  const inputClassName = classes.childInput;

  return (
    <div className={classes.container} test-id={testId}>
      <div className={inputStyle}>
        {children({
          onFocus: onChildFocus,
          onBlur: onChildBlur,
          inputClassName,
        })}
      </div>
      <label className={floatingTitleStyle}>{title}</label>
      <div className={classes.errors}>{error}</div>
    </div>
  );
};

export default Base;
